import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, Mail, Phone, Send } from "lucide-react";

const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const REASONS = {
    unpaid_bill: { label: "Unpaid bill", cls: "bg-red-100 text-red-800" },
    autopay_stopped: { label: "Auto-pay stopped", cls: "bg-amber-100 text-amber-800" },
    no_payment: { label: "No payment this month", cls: "bg-stone-200 text-stone-800" },
};
const thisMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function Row({ r, onRemind }) {
    const [busy, setBusy] = React.useState(false);
    async function remind() {
        setBusy(true);
        try { await onRemind(r); } finally { setBusy(false); }
    }
    return (
        <tr className="border-b border-border/60 align-top" data-testid={`unpaid-row-${r.family_id}`}>
            <td className="p-3">
                <div className="font-medium">{r.parent_name}</div>
                <div className="text-xs text-brand-stone">{r.children.join(", ") || "No children on file"}</div>
            </td>
            <td className="p-3 space-y-1">
                {r.reasons.map(k => <Badge key={k} className={`${REASONS[k].cls} border-0 mr-1`}>{REASONS[k].label}</Badge>)}
                {r.unpaid_bills.map(b => (
                    <div key={b.id} className="text-xs text-brand-stone">{b.month} · {b.description || "Bill"} · {usd(b.amount_cents)}{b.status === "FAILED" ? " · card failed" : ""}</div>
                ))}
            </td>
            <td className="p-3 font-semibold whitespace-nowrap" data-testid={`unpaid-owed-${r.family_id}`}>{r.owed_cents ? usd(r.owed_cents) : "—"}</td>
            <td className="p-3 text-xs">
                <a href={`mailto:${r.parent_email}`} className="flex items-center gap-1 text-primary hover:underline"><Mail className="w-3 h-3" />{r.parent_email}</a>
                {r.parent_phone && <a href={`tel:${r.parent_phone}`} className="flex items-center gap-1 text-brand-stone mt-1"><Phone className="w-3 h-3" />{r.parent_phone}</a>}
            </td>
            <td className="p-3 text-xs text-brand-stone whitespace-nowrap">{r.last_reminder_at ? new Date(r.last_reminder_at).toLocaleDateString() : "Never"}</td>
            <td className="p-3 text-right">
                {r.owed_cents > 0 && (
                    <Button size="sm" variant="outline" onClick={remind} disabled={busy} className="gap-1" data-testid={`unpaid-remind-${r.family_id}`}>
                        <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Remind"}
                    </Button>
                )}
            </td>
        </tr>
    );
}

export default function UnpaidReport() {
    const [month, setMonth] = React.useState(thisMonth());
    const [data, setData] = React.useState(null);
    const [filter, setFilter] = React.useState("all");
    const load = React.useCallback(() => {
        if (month) { api.get("/admin/unpaid", { params: { month } }).then(r => setData(r.data)); }
    }, [month]);
    React.useEffect(() => { load(); }, [load]);

    async function remind(r) {
        try {
            await api.post(`/admin/unpaid/${r.family_id}/remind`);
            toast.success(`Reminder sent to ${r.parent_name}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send reminder"); }
    }
    const rows = (data?.rows || []).filter(r => filter === "all" || r.reasons.includes(filter));
    return (
        <div data-testid="unpaid-report">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="overline text-brand-stone">Payments</div>
                    <h1 className="font-serif text-4xl text-brand-ink mt-2">Who hasn't paid</h1>
                    <p className="text-sm text-brand-stone mt-1">Families with an unpaid bill, stopped auto-pay, or enrolled children with no payment for the month.</p>
                </div>
                <div className="flex items-end gap-4">
                    <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44" data-testid="unpaid-month" />
                    <div className="text-right">
                        <div className="text-[11px] text-brand-stone">Total owed</div>
                        <div className="text-xl font-semibold" data-testid="unpaid-total">{usd(data?.total_owed_cents)}</div>
                    </div>
                </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
                <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")} data-testid="unpaid-filter-all">All ({data?.rows.length ?? 0})</Button>
                {Object.entries(REASONS).map(([k, v]) => (
                    <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} data-testid={`unpaid-filter-${k}`}>
                        {v.label} ({data?.counts?.[k] ?? 0})
                    </Button>
                ))}
            </div>
            <Card><CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-brand-stone">
                        <tr><th className="p-3">Family</th><th className="p-3">Why</th><th className="p-3">Owed</th><th className="p-3">Contact</th><th className="p-3">Last reminder</th><th className="p-3"></th></tr>
                    </thead>
                    <tbody>
                        {rows.map(r => <Row key={r.family_id} r={r} onRemind={remind} />)}
                        {data && rows.length === 0 && (
                            <tr><td colSpan={6} className="p-10 text-center text-brand-stone">Everyone is paid up for this month.</td></tr>
                        )}
                    </tbody>
                </table>
            </CardContent></Card>
            <p className="text-xs text-brand-stone mt-3 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Staff families (teachers/admins) are not listed. Unpaid bills from earlier months stay listed until paid.</p>
        </div>
    );
}
